import { BaseQueryFn } from '@reduxjs/toolkit/query'
import { AxiosRequestConfig } from 'axios'
import api from './api'
import connectivityStore from '@/redux/slices/connectivitySlice'

export const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' }
): BaseQueryFn<
  {
    url: string
    method?: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
    headers?: AxiosRequestConfig['headers']
    responseType?: AxiosRequestConfig['responseType']
    timeout?: AxiosRequestConfig['timeout']
    optionalService?: boolean
  },
  unknown,
  unknown
> =>
  async ({ url, method = 'GET', data, params, headers, responseType, timeout, optionalService }) => {
    try {
      let requestUrl = baseUrl + url
      if (requestUrl.startsWith('/api/')) {
        requestUrl = requestUrl.slice(4)
      }
      const result = await api({
        url: requestUrl,
        method,
        data,
        params,
        headers,
        // Binary endpoints (payroll bank-transfer file, quotation PDF) pass
        // `responseType: 'blob'`; dropping it here made axios parse the binary
        // body as JSON and corrupt every download.
        ...(responseType ? { responseType } : {}),
        ...(timeout !== undefined ? { timeout } : {}),
        ...(optionalService ? { optionalService } : {}),
      })
      // A successful response is also a connectivity observation: the server
      // is up (marking is change-gated, so hot request streams don't thrash
      // subscribers).
      connectivityStore.markServerReachable('up')
      return { data: result.data }
    } catch (axiosError) {
      // Two rejection shapes reach here: a real AxiosError (network layer —
      // has .response) or the response interceptor's rejected BODY (camelized
      // server payload + attached `status`). Preserve the body's field-level
      // errors map either way — flattening it to a message string used to
      // degrade every 422 to a generic "Invalid Data".
      const err = axiosError as any
      const body = err?.response?.data ?? err
      const status = err?.response?.status ?? (typeof body?.status === 'number' ? body.status : undefined)

      // Connectivity observation: the server ANSWERING (success or 4xx) means
      // it is reachable; only missing responses (network layer) and 5xx mark
      // it down. useConnectivity turns this into isEffectivelyOnline so the
      // POS queues sales instead of losing them when the link is up but the
      // server is gone.
      if (optionalService || err?.code === 'ERR_CANCELED') {
        // A model/provider failure or cancellation says nothing about POS
        // reachability. Health probes and core APIs remain authoritative.
      } else if (typeof status === 'number' && status >= 500) {
        connectivityStore.markServerReachable('down')
      } else if (status === undefined) {
        connectivityStore.markServerReachable('down')
      } else {
        connectivityStore.markServerReachable('up')
      }

      return {
        error: {
          status,
          data: body ?? err?.message,
        },
      }
    }
  }
